import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../web/components/Button';
import { Checkbox } from '../../../../web/components/Checkbox';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { Input } from '../../../../web/components-v2/Input';
import { LeadRuleViewModel } from '../../../viewModels/leadrules';
import { Fill } from '../../generics/Fill';
import { Group } from '../../leadView/components/Group';
import { styleSheet } from '../styles';
import { minDaysOptions, ownershipEnforcementOptions, sortCriteriaOptions } from '../utils';
import { AvailableLeads } from './AvailableLeads';
import { generateCustomValueOption, getSelectedOption } from './RuleContainerHeader';
import { SetCustomValueModal } from './SetCustomValueModal';

interface IProps {
	availableLeadsLoading: boolean;
	availableLeadsNumber: number;
	leadRule: LeadRuleViewModel;
	onClickToSave: (values: {
		minDays: number;
		ownership: string;
		preserve: boolean | null;
		primarySortCriteria: 'LastInteraction' | 'Random';
		priority: number;
	}) => void;
}

export const AdditionalSettings: React.FC<IProps> = observer(
	({ availableLeadsLoading, availableLeadsNumber, leadRule, onClickToSave }) => {
		const defaultMinDays = minDaysOptions[0];
		const defaultOwnership = ownershipEnforcementOptions[1];
		const defaultSortCriteria = sortCriteriaOptions[0];

		const [ownership, setOwnership] = useState(defaultOwnership);
		const [preserve, setPreserve] = useState<boolean>(false);
		const [priority, setPriority] = useState(10);
		const [sortCriteria, setSortCriteria] = useState(defaultSortCriteria);

		const [minDays, setMinDays] = useState(defaultMinDays);

		useEffect(() => {
			if (leadRule) {
				const getOwnership = ownershipEnforcementOptions.filter(
					item => item.dataContext === leadRule?.ownershipEnforcement
				)?.[0];
				setOwnership(getOwnership || defaultOwnership);

				setPreserve(leadRule?.preserve === true);

				setPriority(leadRule?.priority);

				setMinDays(getSelectedOption(leadRule?.minDaysSinceLastInteraction, minDaysOptions));

				const getSortCriteria = sortCriteriaOptions.find(item => item.dataContext === leadRule?.primarySortCriteria);
				setSortCriteria(getSortCriteria || defaultSortCriteria);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [leadRule]);

		const handlePriorityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			const parsed = parseInt(event.target.value, 10);
			if (parsed && !isNaN(parsed)) {
				setPriority(parsed);
			} else if (!event.target.value) {
				setPriority(null);
			}
		};

		const handleOwnershipChange = (option: ISelectOption) => {
			setOwnership(option);
			leadRule?.setOwnershipEnforcement(option.dataContext);
		};

		const handleMinDaysChange = (option: ISelectOption) => {
			if (option.id !== 'custom') {
				leadRule?.setMinDaysSinceLastInteraction(option.dataContext);
				setMinDays(option);
				return;
			}
			setMinDays(option);
		};

		const handlePreserveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setPreserve(e.target.checked);
			leadRule?.setPreserve(e.target.checked ? true : false);
		};

		const handleSortCriteriaChange = (option: ISelectOption) => {
			setSortCriteria(option);
			leadRule?.setPrimarySortCriteria(option.dataContext);
		};

		const handleSave = () => {
			leadRule?.setPriority(priority);
			onClickToSave({
				minDays: minDays?.dataContext,
				ownership: ownership?.dataContext,
				preserve: preserve ? true : false,
				primarySortCriteria: sortCriteria?.dataContext,
				priority,
			});
		};

		return (
			<>
				<div className={css(styleSheet.ctaContainer)}>
					<Group vCentered={true} spacingSize={20}>
						<>
							<div className={css(styleSheet.regularText)}>Priority:</div>
							<Input
								styles={[styleSheet.priorityTextInput]}
								id='option-priority-input'
								onChange={handlePriorityChange}
								type='text'
								value={priority}
							/>
						</>
						<>
							<div className={css(styleSheet.regularText)}>Ownership:</div>
							<Select
								value={ownership?.id}
								onValueChange={id => {
									const option = ownershipEnforcementOptions.find(o => o.id === id);
									if (option) handleOwnershipChange(option);
								}}
							>
								<SelectTrigger styles={styleSheet.regularSelect}>
									<span>{ownership?.text ?? 'Select...'}</span>
								</SelectTrigger>
								<SelectContent>
									{ownershipEnforcementOptions.map(option => (
										<SelectItem key={option.id} value={option.id}>
											{option.text}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</>
						<>
							<div className={css(styleSheet.regularText)}>Min days:</div>
							<Select
								value={minDays?.id}
								onValueChange={id => {
									const option = minDaysOptions.find(o => o.id === id);
									if (option) handleMinDaysChange(option);
								}}
							>
								<SelectTrigger styles={styleSheet.timeSelect}>
									<span>{minDays?.text ?? 'Select...'}</span>
								</SelectTrigger>
								<SelectContent>
									{minDaysOptions.map(option => (
										<SelectItem key={option.id} value={option.id}>
											{option.text}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{minDays?.id === 'custom' ? (
								<SetCustomValueModal
									onClickToClose={() =>
										setMinDays(getSelectedOption(leadRule?.minDaysSinceLastInteraction, minDaysOptions))
									}
									onClickToSave={value => {
										setMinDays(generateCustomValueOption(Number(value)));
										leadRule.setMinDaysSinceLastInteraction(Number(value));
									}}
								/>
							) : null}
						</>
						<>
							<div className={css(styleSheet.regularText)}>Sort:</div>
							<Select
								value={sortCriteria?.id}
								onValueChange={id => {
									const option = sortCriteriaOptions.find(o => o.id === id);
									if (option) handleSortCriteriaChange(option);
								}}
							>
								<SelectTrigger styles={styleSheet.regularSelect}>
									<span>{sortCriteria?.text ?? 'Select...'}</span>
								</SelectTrigger>
								<SelectContent>
									{sortCriteriaOptions.map(option => (
										<SelectItem key={option.id} value={option.id}>
											{option.text}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</>
						<Checkbox checked={preserve} onChange={handlePreserveChange} className={css(styleSheet.preserveCheckbox)}>
							<span className={css(styleSheet.checkboxText)}>Save as template</span>
						</Checkbox>
					</Group>
				</div>
				<div className={css(styleSheet.divider)} />
				<div className={css(styleSheet.ctaContainer)}>
					<Group vCentered={true} spacingSize={20}>
						<Button label='Save' onClick={handleSave} />
						<AvailableLeads availableLeadsLoading={availableLeadsLoading} availableLeadsNumber={availableLeadsNumber} />
						<Fill />
					</Group>
				</div>
			</>
		);
	}
);
